import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, ArrowUpRight } from "lucide-react";
import usePublicData from "../../hooks/usePublicData";
import { PROJECTS } from "../../mock";
import { imageUrl } from "../../lib/api";

export default function ProjectsCarousel() {
  const { data: projects } = usePublicData("/projects", PROJECTS);
  const scroller = useRef(null);
  const scroll = (dir) => scroller.current?.scrollBy({ left: dir * 380, behavior: "smooth" });

  const items = projects.slice(0, 8).map((p) => ({
    ...p,
    display: imageUrl(p.cover) || p.image,
    tag: p.tag || p.category,
    link: p.slug ? `/projects/${p.slug}` : "/projects",
  }));

  return (
    <section className="py-24 bg-white overflow-hidden">
      <div className="container-x">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-12 reveal">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <span className="h-px w-10 bg-[#2f9fe6]" />
              <span className="text-[#2f9fe6] tracking-[0.24em] text-xs font-semibold uppercase">Our Work</span>
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0f2740] leading-tight max-w-xl">Recent renovation projects</h2>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => scroll(-1)} className="w-12 h-12 rounded-full border border-[#dfe4e9] flex items-center justify-center text-[#0f2740] hover:bg-[#0f2740] hover:text-white hover:border-[#0f2740] transition-all"><ChevronLeft className="w-5 h-5" /></button>
            <button onClick={() => scroll(1)} className="w-12 h-12 rounded-full border border-[#dfe4e9] flex items-center justify-center text-[#0f2740] hover:bg-[#0f2740] hover:text-white hover:border-[#0f2740] transition-all"><ChevronRight className="w-5 h-5" /></button>
          </div>
        </div>

        <div ref={scroller} className="flex gap-6 overflow-x-auto pb-6 -mx-6 px-6 snap-x" style={{ scrollbarWidth: "none" }}>
          {items.map((p) => (
            <Link to={p.link} key={p.id || p.title} className="snap-start shrink-0 w-[320px] sm:w-[360px] group">
              <div className="shape-soft relative mb-5">
                <img src={p.display} alt={p.title} loading="lazy" decoding="async" className="w-full h-[300px] object-cover transition-transform duration-700 group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0f2740]/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                {p.tag && <span className="absolute top-4 left-4 px-3 py-1 rounded-full bg-white/90 text-[#0f2740] text-xs font-semibold capitalize">{p.tag}</span>}
                <span className="absolute bottom-4 right-4 w-11 h-11 rounded-full bg-[#2f9fe6] flex items-center justify-center text-white opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all"><ArrowUpRight className="w-5 h-5" /></span>
              </div>
              <h3 className="font-display text-xl font-semibold text-[#0f2740] mb-1 group-hover:text-[#2f9fe6] transition-colors">{p.title}</h3>
              <p className="text-[#6b7683] text-sm mb-4">{p.location || p.area}, Edmonton</p>
              <div className="flex gap-6 text-sm border-t border-[#eef1f4] pt-4">
                <div><div className="text-[#9aa4af] text-xs uppercase tracking-wide mb-1">Budget</div><div className="font-semibold text-[#0f2740]">{p.budget || "On request"}</div></div>
                <div><div className="text-[#9aa4af] text-xs uppercase tracking-wide mb-1">Length</div><div className="font-semibold text-[#0f2740]">{p.length || "—"}</div></div>
              </div>
            </Link>
          ))}
        </div>

        <div className="mt-10 text-center reveal">
          <Link to="/projects" className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-[#0f2740] text-white font-semibold hover:bg-[#16324f] transition-colors">View All Projects</Link>
        </div>
      </div>
    </section>
  );
}
